import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import AuthorizationModal from "../components/AuthorizationModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  History, 
  Receipt, 
  CreditCard, 
  Loader2, 
  Search, 
  Eye, 
  Pencil, 
  Trash2, 
  Calendar,
  Building,
  DollarSign,
  FileText,
  Filter,
  RefreshCw,
  Download
} from "lucide-react";

const formatCurrency = (val) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(val || 0);
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function HistorialCuentasPagarPage() {
  const [activeTab, setActiveTab] = useState("facturas");
  const [facturas, setFacturas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Authorization modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null); // { type: 'factura' | 'pago', id: string }
  
  // Filters
  const [filterProveedor, setFilterProveedor] = useState("all");
  const [filterEstado, setFilterEstado] = useState("all");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [page, setPage] = useState(0);
  const [totalFacturas, setTotalFacturas] = useState(0);
  const [totalPagos, setTotalPagos] = useState(0);
  const limit = 20;
  
  // Dialogs
  const [showFacturaDialog, setShowFacturaDialog] = useState(false);
  const [showPagoDialog, setShowPagoDialog] = useState(false);
  const [showDetalleDialog, setShowDetalleDialog] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [selectedPago, setSelectedPago] = useState(null);
  
  // Forms
  const [facturaForm, setFacturaForm] = useState({
    proveedor_id: "",
    numero_factura: "",
    monto_total: 0,
    fecha_factura: "",
    fecha_vencimiento: "",
    descripcion: "",
    categoria: "servicios",
    notas: ""
  });
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params for facturas
      const facturaParams = new URLSearchParams();
      facturaParams.append("skip", page * limit);
      facturaParams.append("limit", limit);
      if (filterProveedor !== "all") facturaParams.append("proveedor_id", filterProveedor);
      if (filterEstado !== "all") facturaParams.append("estado", filterEstado);
      if (filterCategoria !== "all") facturaParams.append("categoria", filterCategoria);
      
      const [factRes, pagosRes, provRes, catRes, metRes] = await Promise.all([
        axios.get(`${API}/cuentas-pagar/facturas?${facturaParams.toString()}`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/pagos?skip=${page * limit}&limit=${limit}`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/proveedores`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/categorias`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/metodos-pago`, getAuthHeaders())
      ]);
      
      setFacturas(factRes.data.items || []);
      setTotalFacturas(factRes.data.total || 0);
      setPagos(pagosRes.data.items || []);
      setTotalPagos(pagosRes.data.total || 0);
      setProveedores(provRes.data.items || []);
      setCategorias(catRes.data || []);
      setMetodosPago(metRes.data || []);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, filterProveedor, filterEstado, filterCategoria]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEditFactura = (factura) => {
    setSelectedFactura(factura);
    setFacturaForm({
      proveedor_id: factura.proveedor_id,
      numero_factura: factura.numero_factura,
      monto_total: factura.monto_total,
      fecha_factura: factura.fecha_factura,
      fecha_vencimiento: factura.fecha_vencimiento,
      descripcion: factura.descripcion || "",
      categoria: factura.categoria,
      notas: factura.notas || ""
    });
    setShowFacturaDialog(true);
  };

  const handleSaveFactura = async () => {
    if (!facturaForm.proveedor_id || !facturaForm.numero_factura || facturaForm.monto_total <= 0) {
      toast.error("Complete todos los campos obligatorios");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/cuentas-pagar/facturas/${selectedFactura.id}`, facturaForm, getAuthHeaders());
      toast.success("Factura actualizada");
      setShowFacturaDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar factura");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFactura = (facturaId) => {
    if (!window.confirm("¿Está seguro de eliminar esta factura? Esta acción requiere autorización.")) return;
    setPendingDeleteAction({ type: 'factura', id: facturaId });
    setShowAuthModal(true);
  };

  const handleDeletePago = (pagoId) => {
    if (!window.confirm("¿Está seguro de eliminar este pago? Esta acción requiere autorización.")) return;
    setPendingDeleteAction({ type: 'pago', id: pagoId });
    setShowAuthModal(true);
  };

  const executeDeleteWithAuth = async (authToken, authorizerName) => {
    if (!pendingDeleteAction) return;
    
    try {
      const { type, id } = pendingDeleteAction;
      if (type === 'factura') {
        await axios.delete(`${API}/cuentas-pagar/facturas/${id}?authorization_token=${authToken}`, getAuthHeaders());
        toast.success(`Factura eliminada. Autorizado por: ${authorizerName}`);
      } else if (type === 'pago') {
        await axios.delete(`${API}/cuentas-pagar/pagos/${id}?authorization_token=${authToken}`, getAuthHeaders());
        toast.success(`Pago eliminado. Autorizado por: ${authorizerName}`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    } finally {
      setPendingDeleteAction(null);
    }
  };

  const viewFacturaDetalle = async (facturaId) => {
    try {
      const res = await axios.get(`${API}/cuentas-pagar/facturas/${facturaId}`, getAuthHeaders());
      setSelectedFactura(res.data);
      setShowDetalleDialog(true);
    } catch (err) {
      toast.error("Error al cargar detalle");
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case "pagada": return <Badge className="bg-green-100 text-green-700">Pagada</Badge>;
      case "parcial": return <Badge className="bg-blue-100 text-blue-700">Parcial</Badge>;
      default: return <Badge className="bg-yellow-100 text-yellow-700">Pendiente</Badge>;
    }
  };

  const clearFilters = () => {
    setFilterProveedor("all");
    setFilterEstado("all");
    setFilterCategoria("all");
    setFilterFechaDesde("");
    setFilterFechaHasta("");
    setSearchTerm("");
    setPage(0);
  };

  // Filter facturas by search term
  const filteredFacturas = facturas.filter(f => 
    !searchTerm || 
    f.numero_factura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.proveedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter pagos by search term
  const filteredPagos = pagos.filter(p => 
    !searchTerm || 
    p.numero_factura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.proveedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.numero_referencia?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 overflow-auto">
        <div className="max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <History className="w-7 h-7 text-[#0C314C]" />Historial de Transacciones
              </h1>
              <p className="text-slate-500 mt-1">Consulta, edita y gestiona todas las transacciones de cuentas por pagar</p>
            </div>
            <Button onClick={fetchData} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Actualizar
            </Button>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <div className="md:col-span-2 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Buscar por factura, proveedor..." 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    className="pl-10"
                  />
                </div>
                <Select value={filterProveedor} onValueChange={v => { setFilterProveedor(v); setPage(0); }}>
                  <SelectTrigger><SelectValue placeholder="Proveedor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los proveedores</SelectItem>
                    {proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterEstado} onValueChange={v => { setFilterEstado(v); setPage(0); }}>
                  <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="parcial">Parcial</SelectItem>
                    <SelectItem value="pagada">Pagada</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterCategoria} onValueChange={v => { setFilterCategoria(v); setPage(0); }}>
                  <SelectTrigger><SelectValue placeholder="Categoría" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {categorias.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" onClick={clearFilters}>
                  <Filter className="w-4 h-4 mr-2" />Limpiar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="facturas" className="flex items-center gap-2">
                <Receipt className="w-4 h-4" />Facturas ({totalFacturas})
              </TabsTrigger>
              <TabsTrigger value="pagos" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />Pagos ({totalPagos})
              </TabsTrigger>
            </TabsList>

            {/* Facturas Tab */}
            <TabsContent value="facturas">
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50 dark:bg-slate-800">
                          <th className="text-left p-4 font-medium text-slate-600">Factura</th>
                          <th className="text-left p-4 font-medium text-slate-600">Proveedor</th>
                          <th className="text-left p-4 font-medium text-slate-600">Categoría</th>
                          <th className="text-left p-4 font-medium text-slate-600">Fecha</th>
                          <th className="text-left p-4 font-medium text-slate-600">Vencimiento</th>
                          <th className="text-right p-4 font-medium text-slate-600">Monto</th>
                          <th className="text-right p-4 font-medium text-slate-600">Pagado</th>
                          <th className="text-center p-4 font-medium text-slate-600">Estado</th>
                          <th className="text-right p-4 font-medium text-slate-600">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFacturas.length === 0 ? (
                          <tr><td colSpan={9} className="p-8 text-center text-slate-500">No hay facturas registradas</td></tr>
                        ) : filteredFacturas.map(factura => (
                          <tr key={factura.id} className="border-b hover:bg-slate-50">
                            <td className="p-4">
                              <p className="font-medium">{factura.numero_factura}</p>
                            </td>
                            <td className="p-4">
                              <p className="font-medium">{factura.proveedor_nombre}</p>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline">{categorias.find(c => c.value === factura.categoria)?.label || factura.categoria}</Badge>
                            </td>
                            <td className="p-4 text-sm">{formatDate(factura.fecha_factura)}</td>
                            <td className="p-4 text-sm">{formatDate(factura.fecha_vencimiento)}</td>
                            <td className="p-4 text-right font-medium">{formatCurrency(factura.monto_total)}</td>
                            <td className="p-4 text-right text-green-600">{formatCurrency(factura.monto_pagado || 0)}</td>
                            <td className="p-4 text-center">{getEstadoBadge(factura.estado)}</td>
                            <td className="p-4 text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => viewFacturaDetalle(factura.id)} title="Ver detalle">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEditFactura(factura)} title="Editar">
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                {factura.estado !== "pagada" && (
                                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteFactura(factura.id)} title="Eliminar">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {totalFacturas > limit && (
                    <div className="flex justify-between items-center p-4 border-t">
                      <span className="text-sm text-slate-500">Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalFacturas)} de {totalFacturas}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                        <Button variant="outline" size="sm" disabled={(page + 1) * limit >= totalFacturas} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pagos Tab */}
            <TabsContent value="pagos">
              <Card>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50 dark:bg-slate-800">
                          <th className="text-left p-4 font-medium text-slate-600">Fecha</th>
                          <th className="text-left p-4 font-medium text-slate-600">Factura</th>
                          <th className="text-left p-4 font-medium text-slate-600">Proveedor</th>
                          <th className="text-left p-4 font-medium text-slate-600">Método</th>
                          <th className="text-left p-4 font-medium text-slate-600">Referencia</th>
                          <th className="text-right p-4 font-medium text-slate-600">Monto</th>
                          <th className="text-left p-4 font-medium text-slate-600">Registrado por</th>
                          <th className="text-right p-4 font-medium text-slate-600">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPagos.length === 0 ? (
                          <tr><td colSpan={8} className="p-8 text-center text-slate-500">No hay pagos registrados</td></tr>
                        ) : filteredPagos.map(pago => (
                          <tr key={pago.id} className="border-b hover:bg-slate-50">
                            <td className="p-4 text-sm">{formatDate(pago.fecha_pago)}</td>
                            <td className="p-4">
                              <p className="font-medium">{pago.numero_factura}</p>
                            </td>
                            <td className="p-4">{pago.proveedor_nombre}</td>
                            <td className="p-4">
                              <Badge variant="outline">{metodosPago.find(m => m.value === pago.metodo_pago)?.label || pago.metodo_pago}</Badge>
                            </td>
                            <td className="p-4 text-sm text-slate-600">{pago.numero_referencia || "-"}</td>
                            <td className="p-4 text-right font-medium text-green-600">{formatCurrency(pago.monto)}</td>
                            <td className="p-4 text-sm text-slate-600">{pago.created_by_name}</td>
                            <td className="p-4 text-right">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeletePago(pago.id)} title="Eliminar">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {totalPagos > limit && (
                    <div className="flex justify-between items-center p-4 border-t">
                      <span className="text-sm text-slate-500">Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalPagos)} de {totalPagos}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                        <Button variant="outline" size="sm" disabled={(page + 1) * limit >= totalPagos} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Edit Factura Dialog */}
        <Dialog open={showFacturaDialog} onOpenChange={setShowFacturaDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle><Pencil className="w-5 h-5 inline mr-2" />Editar Factura</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Proveedor *</Label>
                <Select value={facturaForm.proveedor_id} onValueChange={v => setFacturaForm({...facturaForm, proveedor_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar proveedor..." /></SelectTrigger>
                  <SelectContent>
                    {proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>No. Factura *</Label>
                  <Input value={facturaForm.numero_factura} onChange={e => setFacturaForm({...facturaForm, numero_factura: e.target.value})} />
                </div>
                <div>
                  <Label>Monto Total *</Label>
                  <Input type="number" value={facturaForm.monto_total} onChange={e => setFacturaForm({...facturaForm, monto_total: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Factura</Label>
                  <Input type="date" value={facturaForm.fecha_factura} onChange={e => setFacturaForm({...facturaForm, fecha_factura: e.target.value})} />
                </div>
                <div>
                  <Label>Fecha Vencimiento</Label>
                  <Input type="date" value={facturaForm.fecha_vencimiento} onChange={e => setFacturaForm({...facturaForm, fecha_vencimiento: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Categoría</Label>
                <Select value={facturaForm.categoria} onValueChange={v => setFacturaForm({...facturaForm, categoria: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categorias.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea value={facturaForm.descripcion} onChange={e => setFacturaForm({...facturaForm, descripcion: e.target.value})} />
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={facturaForm.notas} onChange={e => setFacturaForm({...facturaForm, notas: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFacturaDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveFactura} disabled={saving} className="bg-[#0C314C] text-white">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detalle Factura Dialog */}
        <Dialog open={showDetalleDialog} onOpenChange={setShowDetalleDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle><FileText className="w-5 h-5 inline mr-2" />Detalle de Factura</DialogTitle>
            </DialogHeader>
            {selectedFactura && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Proveedor</p>
                    <p className="font-medium">{selectedFactura.proveedor_nombre}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">No. Factura</p>
                    <p className="font-medium">{selectedFactura.numero_factura}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Monto Total</p>
                    <p className="font-medium text-lg">{formatCurrency(selectedFactura.monto_total)}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Saldo Pendiente</p>
                    <p className="font-medium text-lg text-red-600">{formatCurrency(selectedFactura.saldo_pendiente)}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Fecha Vencimiento</p>
                    <p className="font-medium">{formatDate(selectedFactura.fecha_vencimiento)}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Estado</p>
                    {getEstadoBadge(selectedFactura.estado)}
                  </div>
                </div>
                {selectedFactura.descripcion && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Descripción</p>
                    <p>{selectedFactura.descripcion}</p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium mb-2">Historial de Pagos</h4>
                  {selectedFactura.pagos?.length === 0 ? (
                    <p className="text-slate-500 text-sm">No hay pagos registrados</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedFactura.pagos?.map(pago => (
                        <div key={pago.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div>
                            <p className="font-medium text-green-700">{formatCurrency(pago.monto)}</p>
                            <p className="text-xs text-slate-500">
                              {formatDate(pago.fecha_pago)} - {metodosPago.find(m => m.value === pago.metodo_pago)?.label || pago.metodo_pago}
                            </p>
                          </div>
                          {pago.numero_referencia && <Badge variant="outline">{pago.numero_referencia}</Badge>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetalleDialog(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Authorization Modal */}
        <AuthorizationModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            setPendingDeleteAction(null);
          }}
          onAuthorized={executeDeleteWithAuth}
          title="Autorización para Eliminar"
          description="Para eliminar esta transacción financiera, ingrese las credenciales de un usuario autorizado."
          getAuthHeaders={getAuthHeaders}
        />
      </main>
    </div>
  );
}
